import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const Header = ({ user: propUser = null }) => {
  const { user: authUser, signOut } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Use prop user if provided, otherwise use auth user
  const user = propUser || (authUser ? { type: 'user' } : null);

  const navigationItems = [
    { label: 'Inicio', path: '/landing-page', icon: 'Home', authRequired: false },
    { label: 'Explorar', path: '/business-directory', icon: 'Search', authRequired: true, userTypes: ['user'] },
    { label: 'Mi Panel', path: '/user-dashboard', icon: 'LayoutDashboard', authRequired: true, userTypes: ['user'] },
    { label: 'Panel de Afiliados', path: '/affiliate-dashboard', icon: 'Award', authRequired: true, userTypes: ['user'] },
    { label: 'Panel Empresarial', path: '/business-dashboard', icon: 'Briefcase', authRequired: true, userTypes: ['business'] },
  ];

  const isActive = (path) => location?.pathname === path;

  const getVisibleItems = () => {
    return navigationItems?.filter(item => {
      if (!item?.authRequired) return true;
      if (!user) return false;
      if (!item?.userTypes) return true;
      return item?.userTypes?.includes(user?.type);
    });
  };

  const visibleItems = getVisibleItems();

  const getNotificationCount = (path) => {
    if (!user) return 0;
    if (path === '/user-dashboard') return user?.notifications?.dashboard || 0;
    if (path === '/affiliate-dashboard') return user?.notifications?.affiliate || 0;
    return 0;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login-selection');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <>
      {/* Desktop Header */}
      <header className="sticky top-0 z-50 w-full bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/landing-page" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
              <Icon name="Zap" size={32} className="text-primary" />
              <span className="text-2xl font-bold text-primary">BIZZNET</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {visibleItems?.map((item) => (
                <Link
                  key={item?.path}
                  to={item?.path}
                  className={`relative flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                    isActive(item?.path)
                      ? 'bg-primary text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                  {getNotificationCount(item?.path) > 0 && (
                    <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full">
                      {getNotificationCount(item?.path)}
                    </span>
                  )}
                </Link>
              ))}
            </nav>

            {/* Auth Actions */}
            <div className="hidden lg:flex items-center gap-4">
              {!authUser && (
                <Link 
                  to="/login-selection" 
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  <Icon name="LogIn" size={18} />
                  <span>INICIAR SESIÓN</span>
                </Link>
              )}
              {authUser && (
                <>
                  <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                    <Icon name={user?.type === 'business' ? 'Briefcase' : 'User'} size={16} className="text-primary" />
                    <span className="text-sm font-medium text-gray-700">
                      {user?.type === 'business' ? 'Empresa' : user?.membershipTier || 'Explorador'}
                    </span>
                  </div>
                  <button
                    onClick={handleSignOut}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors duration-200"
                  >
                    <Icon name="LogOut" size={18} />
                    <span>CERRAR SESIÓN</span>
                  </button>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden flex items-center justify-center w-10 h-10 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
            onClick={closeMobileMenu}
          />
          
          {/* Mobile Menu */}
          <div className="fixed top-0 right-0 bottom-0 w-80 max-w-full bg-white shadow-2xl z-50 lg:hidden overflow-y-auto">
            {/* Mobile Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Icon name="Zap" size={32} className="text-primary" />
                <span className="text-2xl font-bold text-primary">BIZZNET</span>
              </div>
              <button 
                onClick={closeMobileMenu} 
                className="flex items-center justify-center w-10 h-10 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Close menu"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            {/* Mobile Auth Section */}
            {!authUser && (
              <div className="p-4 border-b border-gray-200">
                <Link 
                  to="/login-selection" 
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-colors duration-200"
                  onClick={closeMobileMenu}
                >
                  <Icon name="LogIn" size={18} />
                  <span>INICIAR SESIÓN</span>
                </Link>
              </div>
            )}

            {authUser && (
              <div className="p-4 border-b border-gray-200">
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg">
                  <Icon name={user?.type === 'business' ? 'Briefcase' : 'User'} size={16} className="text-primary" />
                  <span className="text-sm font-medium text-gray-700">
                    {user?.type === 'business' ? 'Empresa' : user?.membershipTier || 'Explorador'}
                  </span>
                </div>
              </div>
            )}

            {/* Mobile Navigation - Solo mostrar cuando hay usuario autenticado */}
            {authUser && (
              <nav className="p-2">
                {visibleItems?.map((item) => (
                  <Link
                    key={item?.path}
                    to={item?.path}
                    className={`relative flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 mb-1 ${
                      isActive(item?.path)
                        ? 'bg-primary text-white shadow-md'
                        : 'text-gray-700 hover:bg-gray-100 hover:text-primary'
                    }`}
                    onClick={closeMobileMenu}
                  >
                    <Icon name={item?.icon} size={20} />
                    <span>{item?.label}</span>
                    {getNotificationCount(item?.path) > 0 && (
                      <span className="ml-auto flex items-center justify-center min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full">
                        {getNotificationCount(item?.path)}
                      </span>
                    )}
                  </Link>
                ))}
              </nav>
            )}

            {/* Botón Cerrar Sesión en Mobile - Solo cuando hay usuario autenticado */}
            {authUser && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    handleSignOut();
                    closeMobileMenu();
                  }}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-red-500 hover:bg-red-600 text-white font-medium rounded-lg transition-colors duration-200"
                >
                  <Icon name="LogOut" size={18} />
                  <span>CERRAR SESIÓN</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export default Header;